import React, { SyntheticEvent } from 'react';
import { useController } from 'react-hook-form';
import { MaskedInput, useFormContext } from '@alfa-retail-react/components';

import { WEB_VISOR_DISABLE } from 'app/utils/AnalyticsUtils';
import { InputFieldEventHandler } from 'app/form/InputFieldEventHandler';
import { IntroductionFormStore } from 'app/logic/pages/introduction/IntroductionFormStore';
import { IntroductionFormValues } from 'app/logic/pages/introduction/IntroductionPage';
import { ValidationRules } from 'app/form/ValidationRules';

export interface PhoneFormItemProps {
    /**
     * Хранилище данных для страницы выбора параметров
     */
    formStore: IntroductionFormStore;
}

const FULL_PHONE_NUMBER_LENGTH = '+375CCXXXXXXX'.length;
const FULL_MASKED_NUMBER_LENGTH = '+375 (CC) XXX-XX-XX'.length;

const isAutofillPasteEvent = (event: SyntheticEvent) => {
    const { inputType, data } = event.nativeEvent;

    if (!inputType || !data) {
        return false;
    }

    const isInsert = inputType === 'insertFromPaste';
    const isFullNumberProvided =
        data.length === FULL_PHONE_NUMBER_LENGTH ||
        data.length === FULL_MASKED_NUMBER_LENGTH;

    return isInsert && isFullNumberProvided;
};

export const PHONE_FORM_ITEM_NAME = 'phone';
const DEFAULT_PHONE_NUMBER_PREFIX = '+375';
const PHONE_MASK = [
    '+',
    '3',
    '7',
    '5',
    ' ',
    '(',
    /\d/,
    /\d/,
    ')',
    ' ',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
];

const unmaskPhoneNumber = (masked: string): string => {
    const match = masked.match(ValidationRules.phoneNumberRegex);

    if (!match) {
        return masked;
    }

    const [full, countryCode, operatorCode, first, second, third] = match;
    return [countryCode, operatorCode, first, second, third].join('');
};

/**
 * Поле формы для ввода телефонного номера РБ.
 */
export const PhoneFormItem = ({ formStore }: PhoneFormItemProps) => {
    const PLACEHOLDER = 'Номер мобильного телефона';
    const HINT = 'В формате +375 (ХХ) ХХХ-ХХ-ХХ';

    const { control, handleMultipleErrors, setFieldElement, trigger } =
        useFormContext<IntroductionFormValues>();
    const { field, fieldState } = useController<IntroductionFormValues>({
        name: PHONE_FORM_ITEM_NAME,
        control,
        defaultValue: formStore.phone ?? DEFAULT_PHONE_NUMBER_PREFIX,
        rules: {
            required: ValidationRules.required(),
            pattern: ValidationRules.phoneNumber(),
            validate: ValidationRules.phoneNumberValidations(),
        },
    });

    const fieldHandler = new InputFieldEventHandler<IntroductionFormValues>(
        trigger,
    );
    const { onBlur, onClear, onChange } = fieldHandler.getEventHandlers(
        field,
        PHONE_FORM_ITEM_NAME,
        (value) => (formStore.phone = unmaskPhoneNumber(value)),
        false,
    );

    const handleOnChangeWithPaste = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (isAutofillPasteEvent(event)) {
            onChange({
                ...event,
                target: {
                    ...event.target,
                    value: event.nativeEvent.data,
                },
            });
        } else {
            onChange(event);
        }
    };

    return (
        <MaskedInput
            clear
            mask={PHONE_MASK}
            ref={(element) => {
                field.ref(element);
                setFieldElement(PHONE_FORM_ITEM_NAME, element);
            }}
            type="tel"
            value={field.value}
            size="l"
            inputClassName={WEB_VISOR_DISABLE}
            label={PLACEHOLDER}
            labelClassName="label"
            hint={HINT}
            error={handleMultipleErrors(fieldState.error)}
            onBlur={onBlur}
            onClear={onClear}
            onChange={handleOnChangeWithPaste}
        />
    );
};
